import React, { useEffect, useMemo, useState } from 'react';
import { Collapse, IconButton, Tooltip } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import {
    Container, Block, CategoryItem, SubcategoryItem,
    CollapseContent, InfoText, PermissionsPanel,
    MiddleDivider, BlockTitle, BlockHeader,
    ScrollableCategoryList,
    SubcategoryItemText
} from './Permissions.styles';
import { Permission, PermissionEntityType } from '../../types/permissionType';
import { useGroupsData } from '../../store/selectors/groupSelectors';
import { useActionsData } from '../../store/selectors/actionModulesSelectors';
import { useServiceModules } from '../../store/selectors/serviceModulesSelector';
import { useAllPermissions } from '../../store/selectors/permissionsSelector';
import { useAppDispatch } from '../../hooks/StoreHooks';
import {
    addEntityPermissionThunk, addGeneralPermissionThunk, fetchUserPermissionsThunk,
    removeEntityPermissionThunk, removeGeneralPermissionThunk
} from '../../store/thunk/permissionsThunk';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useTranslation } from 'react-i18next';
import UserListBlock from './PermissionSettings/UserListBlock';
import { CompanyManagementUser } from '../../types/companyManagementTypes';
import { useCompanyManagementUsers } from '../../store/selectors/companyManagementSelector';
import { fetchCompanyManagementUsersThunk } from '../../store/thunk/companyManagementThunk';
import GeneralPermissionList from './PermissionSettings/GeneralPermissionList';
import PerEntityPermissionList from './PermissionSettings/PerEntityPermissionList';
import PermissionCheckbox from './PermissionSettings/PermissionCheckbox';
import { useSubCompaniesData } from '../../store/selectors/subCompaniesSelector';
import { useProfileData } from '../../store/selectors/profileSelector';

const PermissionSettings = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
    const [userPermissionList, setUserPermissionList] = useState<Record<string, Record<string, boolean>>>({});
    const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

    const fullPermissionList = useAllPermissions();
    const groups = useGroupsData();
    const actionModules = useActionsData();
    const serviceModules = useServiceModules();
    const subCompanies = useSubCompaniesData();

    const users = useCompanyManagementUsers();
    const { userHasPermission } = usePermissionChecker();
    const canEditPermissions = userHasPermission(PermissionEntityType.Permissions, "Edit");
    const currentUserId = useProfileData()?.id;

    // BON-1852: users must not be able to change their own permission-management rights
    const isOwnPermissionsEntityType = (entityType: string) => {
        return selectedUserId === currentUserId && entityType === PermissionEntityType.Permissions;
    }

    const entityTypeToCategoryMap: Partial<Record<PermissionEntityType, string>> = {
        [PermissionEntityType.Groups]: "Groups",
        [PermissionEntityType.ActionModules]: "Action Modules",
        [PermissionEntityType.ServiceModules]: "Service Modules",
        [PermissionEntityType.SubCompanies]: "Sub Companies",
    };

    const categories = [
        "General",
        ...Array.from(new Set(Object.values(entityTypeToCategoryMap))),
    ];
    const getCategory = (entityType: string): string => {
        return entityTypeToCategoryMap[entityType] || "General";
    };

    const subcategoryMap = {
        "Groups": groups,
        "Action Modules": actionModules,
        "Service Modules": serviceModules,
        "Sub Companies": subCompanies,
    };
    const getSubcategoriesForCategory = (category: string) => subcategoryMap[category] ?? [];

    const categorizedPermissions = useMemo(() => {
        return fullPermissionList.reduce<Record<
            string,
            {
                perEntity: Permission[];
                global: Permission[];
            }
        >>((acc, perm) => {
            const category = getCategory(perm.entityType);
            if (!acc[category]) {
                acc[category] = { perEntity: [], global: [] };
            }
            if ((perm as any).isPerEntity) {
                acc[category].perEntity.push(perm);
            } else {
                acc[category].global.push(perm);
            }
            return acc;
        }, {});
    }, [fullPermissionList]);

    useEffect(() => {
        dispatch(fetchCompanyManagementUsersThunk());
    }, [dispatch])

    const parsePermissions = (permissionsArray: Permission[]): Record<string, boolean> => {
        return permissionsArray.reduce((acc, perm) => {
            const key = perm.targetEntityId !== null
                ? `${perm.entityType}.${perm.permissionName}.${perm.targetEntityId}`
                : `${perm.entityType}.${perm.permissionName}`;
            acc[key] = true;
            return acc;
        }, {} as Record<string, boolean>);
    };

    const handleUserClick = (user: CompanyManagementUser) => {
        setSelectedUserId(user.id);
        dispatch(fetchUserPermissionsThunk(user.id)).then((result) => {
            if (fetchUserPermissionsThunk.fulfilled.match(result)) {
                const permissionsArray = result.payload.permissions ?? [];
                setUserPermissionList((prev) => ({
                    ...prev,
                    [user.id]: parsePermissions(permissionsArray)
                }));
            }
        });
    };

    const handleCategoryClick = (category: string) => {
        setSelectedCategory(category);
        setExpandedCategory((prev) => (prev === category ? null : category));
        if (selectedSubcategory) {
            setSelectedSubcategory(null);
        }
    };

    const handleSubcategoryClick = (subcategory: string) => {
        setSelectedSubcategory(subcategory);
    };

    const handleCheckboxChange = async (
        permissionName: string,
        entityType: string,
        isPerEntity: boolean,
        targetEntityId?: number
    ) => {
        if (!selectedUserId) return;
        if (isOwnPermissionsEntityType(entityType)) return;
        const key = targetEntityId
            ? `${entityType}.${permissionName}.${targetEntityId}`
            : `${entityType}.${permissionName}`;
        const userPermissions = userPermissionList[selectedUserId] || {};
        const currentValue = userPermissions[key] ?? false;

        setUserPermissionList((prev) => ({
            ...prev,
            [selectedUserId]: {
                ...userPermissions,
                [key]: !currentValue,
            },
        }));

        try {
            if (isPerEntity) {
                const thunk = currentValue ? removeEntityPermissionThunk : addEntityPermissionThunk;
                await dispatch(thunk({
                    userId: selectedUserId,
                    entityType: entityType as PermissionEntityType,
                    permissionName,
                    targetEntityId: targetEntityId!,
                })).unwrap();
            } else {
                const thunk = currentValue ? removeGeneralPermissionThunk : addGeneralPermissionThunk;
                await dispatch(thunk({
                    userId: selectedUserId,
                    entityType: entityType as PermissionEntityType,
                    permissionName,
                })).unwrap();

                setUserPermissionList(prev => {
                    const updated = { ...(prev[selectedUserId] ?? {}) };
                    const generalKey = `${entityType}.${permissionName}`;
                    updated[generalKey] = !currentValue;
                    return { ...prev, [selectedUserId]: updated };
                });
            }
        } catch (err) {
            console.error("Failed to update permission", err);
        }
    };

    return (
        <Container>
            <Block blockPosition="left">
                <UserListBlock
                    users={users}
                    selectedUserId={selectedUserId}
                    onUserClick={handleUserClick}
                />
            </Block>
            <MiddleDivider />
            <Block blockPosition="middle">
                <BlockHeader>
                    <BlockTitle>{t("permissionsPage.categoriesTitle")}</BlockTitle>
                </BlockHeader>
                {!selectedUserId && (
                    <InfoText variant="subtitle2" style={{ fontStyle: "italic", color: "#999" }}>
                        {t("permissionsPage.selectUser")}
                    </InfoText>
                )}
                <ScrollableCategoryList>
                    {selectedUserId && categories.map((category) => (
                        <React.Fragment key={category}>
                            <CategoryItem
                                selected={category === selectedCategory}
                                onClick={() => handleCategoryClick(category)}
                            >
                                {t(`permissionsPage.permissionType.${category}`)}
                                {category !== "General" && (
                                    <IconButton size="small">
                                        {expandedCategory === category ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                )}
                            </CategoryItem>
                            {category !== "General" && (
                                <Collapse in={expandedCategory === category} unmountOnExit>
                                    <CollapseContent>
                                        {getSubcategoriesForCategory(category).map((item) => (
                                            <Tooltip title={item.name} key={item.id}>
                                                <SubcategoryItem
                                                    selected={selectedSubcategory === String(item.id)}
                                                    onClick={() => handleSubcategoryClick(String(item.id))}
                                                >
                                                    <SubcategoryItemText >
                                                        {item.name}
                                                    </SubcategoryItemText>
                                                </SubcategoryItem>
                                            </Tooltip>
                                        ))}
                                    </CollapseContent>
                                </Collapse>
                            )}
                        </React.Fragment>
                    ))}
                </ScrollableCategoryList>
            </Block>
            <MiddleDivider />
            <Block blockPosition="right">
                <BlockHeader>
                    <BlockTitle>{t("permissionsPage.elementsTitle")}</BlockTitle>
                </BlockHeader>
                {selectedUserId && selectedCategory ? (
                    <PermissionsPanel>
                        {!selectedSubcategory && (
                            selectedCategory === "General" ?
                                <GeneralPermissionList
                                    permissions={categorizedPermissions["General"]?.global ?? []}
                                    checkedMap={userPermissionList[selectedUserId!] ?? {}}
                                    onCheckboxToggle={(perm) =>
                                        handleCheckboxChange(perm.permissionName, perm.entityType, false)
                                    }
                                    disabled={!canEditPermissions}
                                    isPermissionLocked={(perm) =>
                                        isOwnPermissionsEntityType(perm.entityType)
                                    }
                                /> :
                                categorizedPermissions[selectedCategory]?.global.map((perm) => {
                                    const key = `${perm.entityType}.${perm.permissionName}`;
                                    const checked = userPermissionList[selectedUserId!]?.[key] || false;

                                    return (
                                        <PermissionCheckbox
                                            key={key}
                                            permission={perm}
                                            checked={checked}
                                            disabled={!canEditPermissions}
                                            onChange={() =>
                                                handleCheckboxChange(perm.permissionName, perm.entityType, false)
                                            }
                                        />
                                    );
                                })
                        )}
                        {selectedSubcategory && (() => {
                            const entityList = getSubcategoriesForCategory(selectedCategory);
                            const matchedEntity = entityList.find(
                                (entity) => String(entity.id) === selectedSubcategory
                            );
                            if (!matchedEntity) return null;

                            return (
                                <PerEntityPermissionList
                                    permissions={categorizedPermissions[selectedCategory]?.perEntity ?? []}
                                    entity={matchedEntity}
                                    checkedMap={userPermissionList[selectedUserId!] ?? {}}
                                    onCheckboxToggle={(perm, _isPerEntity, entityId) =>
                                        handleCheckboxChange(perm.permissionName, perm.entityType, true, entityId)
                                    }
                                    disabled={!canEditPermissions}
                                />
                            );
                        })()}
                    </PermissionsPanel>
                ) : (
                    <InfoText
                        variant="subtitle2"
                        style={{ fontStyle: "italic", color: "#999" }}
                    >
                        {t("permissionsPage.selectCategory")}
                    </InfoText>
                )}
            </Block>
        </Container>
    );
}

export default PermissionSettings
